import React, { Component } from "react";
import { connect } from "react-redux";
import { Badge } from "antd";
import { BellTwoTone } from "@ant-design/icons";
import NotificationDrawer from "../../../component/common/Notification/NotificationDrawer";
import {
    getNotificationsCount,
    getNotifications,
    loadMoreNotifications,
} from "../../../redux/actions/notification/notification";
import notificationSound from "./sms_notification.mp3";
import "../../../component/common/Notification/Notification.css";

class Notifications extends Component {
    state = {
        showDrawer: false,
        notificationCurrentPage: 1,
    };

    // internal refs
    _pollTimeout = null;
    _staffWatcher = null;
    _audio = null;
    _lastStaffCount = null;
    _currentIntervalMs = null;
    _unmounted = false;

    componentDidMount() {
        // initial fetches
        this.props.getNotificationsCount();
        this.props.getNotifications();

        // start polling with current staff count
        const initialCount = this.getStaffCountSafe();
        this._lastStaffCount = initialCount;
        this._currentIntervalMs = this.computeIntervalMs(initialCount);
        this.scheduleNextPoll();

        // cross-tab updates when localStorage "staffs" changes
        window.addEventListener("storage", this.onStorage);

        // same-tab watchdog (in case code mutates localStorage without Redux)
        this.scheduleStaffCheck();

        // pause/resume when tab visibility changes
        document.addEventListener("visibilitychange", this.onVisibilityChange);
    }


    //check number of staffs every 3 hours
    scheduleStaffCheck = () => {
        clearTimeout(this._staffWatcher);
        const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

        const tick = async () => {
            await this.checkStaffCountChange();
            this._staffWatcher = setTimeout(tick, THREE_HOURS_MS);
        };

        this._staffWatcher = setTimeout(tick, THREE_HOURS_MS);
    };




    componentDidUpdate(prevProps) {
        // sound + refresh list when unread increases
        const prevUnread = prevProps?.unreadNotifications ?? 0;
        const nextUnread = this.props?.unreadNotifications ?? 0;

        if (prevUnread < nextUnread) {
            this.playNotificationSound();
            // this.props.getNotifications();
            this.setState({ notificationCurrentPage: 1 });
        }

        // update title when unread changes
        if (prevUnread !== nextUnread) {
            document.title =
                nextUnread === 0
                    ? "Dashbooking Partner App"
                    : `(${nextUnread}) Dashbooking Partner App`;
        }

        // prefer Redux staffs if available to auto-adjust interval
        const prevCount = Array.isArray(prevProps.staffs) ? prevProps.staffs.length : null;
        const nextCount = Array.isArray(this.props.staffs) ? this.props.staffs.length : null;
        if (prevCount !== nextCount && nextCount != null) {
            this.updateIntervalForCount(nextCount);
        }
    }

    componentWillUnmount() {
        this._unmounted = true;
        if (this._pollTimeout) clearTimeout(this._pollTimeout);
        if (this._staffWatcher) clearInterval(this._staffWatcher);
        window.removeEventListener("storage", this.onStorage);
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }

    /* ---------- Polling ---------- */

    scheduleNextPoll = () => {
        if (this._unmounted) return;
        if (this._pollTimeout) clearTimeout(this._pollTimeout);

        this._pollTimeout = setTimeout(async () => {
            try {
                await this.props.getNotificationsCount();
            } finally {
                // re-check interval in case staff count changed quietly
                const countNow = this.getStaffCountSafe();
                if (countNow !== this._lastStaffCount) {
                    this.updateIntervalForCount(countNow);
                }
                // schedule next tick
                this.scheduleNextPoll();
            }
        }, this._currentIntervalMs);
    };

    computeIntervalMs = (count) => {
        if (count >= 1 && count <= 4) return 300000; // 5 min
        if (count >= 5 && count <= 7) return 120000; // 2 min
        if (count > 7 && count <= 10) return 60000; // 1 min
        if (count > 10) return 30000; // 30 sec
        return 180000; // default for 0/unknown
    };

    updateIntervalForCount = (count) => {
        const nextMs = this.computeIntervalMs(count);
        this._lastStaffCount = count;
        if (nextMs !== this._currentIntervalMs) {
            this._currentIntervalMs = nextMs;
            this.scheduleNextPoll(); // reschedule with new cadence
        }
    };

    /* ---------- Staff count sources ---------- */

    getStaffCountSafe = () => {
        // Prefer Redux prop if your store provides it
        if (Array.isArray(this.props.staffs)) return this.props.staffs.length;

        // Fallback to localStorage
        try {
            const raw = localStorage.getItem("staffs");
            const arr = raw ? JSON.parse(raw) : [];
            return Array.isArray(arr) ? arr.filter(staff => staff.bookingOnline === true).length : 0;
        } catch {
            return 0;
        }
    };

    onStorage = (e) => {
        if (e.key === "staffs") {
            const count = this.getStaffCountSafe();
            if (count !== this._lastStaffCount) {
                this.updateIntervalForCount(count);
            }
        }
    };

    checkStaffCountChange = () => {
        const count = this.getStaffCountSafe();
        if (count !== this._lastStaffCount) {
            this.updateIntervalForCount(count);
        }
    };

    /* ---------- Visibility pause/resume ---------- */

    onVisibilityChange = () => {
        if (document.hidden) {
            if (this._pollTimeout) clearTimeout(this._pollTimeout);
        } else {
            // resume with current cadence and do an immediate fetch
            this.props.getNotificationsCount();
            this.scheduleNextPoll();
        }
    };

    /* ---------- Audio ---------- */

    playNotificationSound = () => {
        try {
            if (window.location.pathname.includes("/pin-auth")) {
                return;
            }
            const muted = JSON.parse(localStorage.getItem('notificationSoundMuted')) || false;
            if (muted) return;
            if (!this._audio) this._audio = new Audio(notificationSound);
            this._audio.currentTime = 0;
            this._audio.play().catch(() => {
                // browsers may block autoplay; ignore
            });
        } catch {
            // ignore audio errors
        }
    };

    /* ---------- UI handlers ---------- */

    handleDrawer = (open) => {
        this.setState({ showDrawer: open });
    };

    // onLoadMore = () => {
    //     this.setState(
    //         (s) => ({ notificationCurrentPage: s.notificationCurrentPage + 1 }),
    //         () => this.props.loadMoreNotifications(this.state.notificationCurrentPage)
    //     );
    // };

    render() {
        const { showDrawer } = this.state;
        const { history, unreadNotifications } = this.props;
        const badgeCount = Number(unreadNotifications || 0);

        return (
            <div>
                <Badge
                    className="notification-button"
                    count={badgeCount}
                    onClick={() => this.handleDrawer(true)}
                >
                    <BellTwoTone style={{ fontSize: "1.5em" }} />
                </Badge>

                {showDrawer && (
                    <NotificationDrawer
                        showDrawer={showDrawer}
                        handleDrawer={this.handleDrawer}
                        // onLoadMore={this.onLoadMore}
                        history={history}
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    error: state.notification.error, // was 'errror'
    message: state.notification.message,
    countNotifications: state.notification.count,
    unreadNotifications: state.notification?.count?.unreadNotifications ?? 0,
    // Optional: if you have staffs in Redux, expose them to auto-adjust cadence:
    staffs: state.staff?.staffs, // adjust to your actual state shape
});

const mapDispatchToProps = (dispatch) => ({
    getNotificationsCount: () => dispatch(getNotificationsCount()),
    getNotifications: () => dispatch(getNotifications()),
    loadMoreNotifications: (page) => dispatch(loadMoreNotifications(page)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Notifications);